// The model runtime the agent loop streams through.
//
// pi-agent-core stopped shipping a default stream function in 0.83.0: `Agent`
// now throws at CONSTRUCTION unless the host either passes `streamFn` per
// agent or installs one process-wide. That was deliberate on the library's
// part — it lets pi-agent-core avoid depending on a provider catalog — which
// makes naming the runtime the host's job.
//
// Installed from the module that builds the agent runtime rather than from
// `main.jsx`, on purpose. A boot-order install covers only the SPA entry, and
// every other entry — the test suite, a worker, any future entry point — would
// construct Agents against an empty default and throw. Module-load coupling
// means whoever can reach `Agent` has already reached this.
//
// The runtime retries one model call, never the run:
//
//   - A call that fails before any content reaches the caller is retried when
//     the failure is transient (a dropped connection, an overloaded provider).
//   - A call that goes quiet is abandoned and retried whether or not content
//     had reached the caller. Providers stall mid-answer with the connection
//     open: keepalive frames keep the socket up, no events arrive, and nothing
//     downstream ends the call before the backend's whole-request timeout
//     kills the run. The first content event of such a retry carries
//     `restart: true`; the transcript reducers drop what the abandoned attempt
//     streamed into the message, since the retry rewrites it from the top.
//     The model context needs nothing: the agent loop replaces its partial
//     message with each event's `partial`.
//   - Any other failure after content has reached the caller stands, since a
//     retry would restate an answer the person has already read.
import { setDefaultStreamFn } from "@earendil-works/pi-agent-core";
import { createAssistantMessageEventStream } from "@earendil-works/pi-ai";
import { streamSimple } from "@earendil-works/pi-ai/compat";
import { isTransientLlmFailure } from "./llmErrorCopy.js";

const MAX_ATTEMPTS = 3;
const BASE_RETRY_DELAY_MS = 1000;
// Tool arguments stream as they are generated, so a minute without an event is
// a stalled provider, not a slow one.
export const STREAM_IDLE_MS = 60_000;
export const STREAM_STALLED_MESSAGE = `No response from the model provider for ${STREAM_IDLE_MS / 1000} seconds`;

function retryDelayMs(attempt) {
    return BASE_RETRY_DELAY_MS * 2 ** (attempt - 1);
}

function sleep(ms, signal) {
    return new Promise((resolve) => {
        if (signal?.aborted) {
            resolve();
            return;
        }
        const timer = setTimeout(resolve, ms);
        signal?.addEventListener(
            "abort",
            () => {
                clearTimeout(timer);
                resolve();
            },
            { once: true },
        );
    });
}

function isTerminal(event) {
    return event?.type === "done" || event?.type === "error";
}

function restatedTerminal(previous, error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    return { ...previous, error: { ...previous.error, errorMessage } };
}

// The abandoned call's own terminal says "aborted", which the agent loop and
// the run hosts read as the person's stop. A stall the retries could not get
// past is a failure, and says so.
function stalledTerminal(terminal) {
    return {
        ...terminal,
        reason: "error",
        error: { ...terminal.error, stopReason: "error", errorMessage: STREAM_STALLED_MESSAGE },
    };
}

// One call to the provider, cancelled either by the caller or by the idle
// watchdog; `stalled` records which. The watchdog is re-armed by every event
// the call produces and disarmed with `release` once the call has ended.
async function openAttempt(model, context, options) {
    const controller = new AbortController();
    const attempt = { stalled: false, stream: null, timer: null };
    const onCallerAbort = () => controller.abort();
    if (options?.signal?.aborted) controller.abort();
    else options?.signal?.addEventListener("abort", onCallerAbort, { once: true });
    attempt.touch = () => {
        if (attempt.timer != null) clearTimeout(attempt.timer);
        attempt.timer = setTimeout(() => {
            attempt.timer = null;
            attempt.stalled = true;
            controller.abort();
        }, STREAM_IDLE_MS);
    };
    attempt.release = () => {
        if (attempt.timer != null) clearTimeout(attempt.timer);
        attempt.timer = null;
        options?.signal?.removeEventListener("abort", onCallerAbort);
    };
    try {
        attempt.stream = await streamSimple(model, context, { ...options, signal: controller.signal });
    } catch (error) {
        attempt.release();
        throw error;
    }
    attempt.touch();
    return attempt;
}

// Forward one attempt's events to the caller until it ends. The opening event
// is held back until content follows it, so an attempt abandoned before any
// content never opens a reply; once content has reached the caller, later
// attempts' opening events are dropped, since the reply is already open.
async function drain(attempt, out, state) {
    const held = [];
    for await (const event of attempt.stream) {
        attempt.touch();
        if (isTerminal(event)) return { terminal: event, held };
        if (event?.type === "start") {
            if (!state.emitted) held.push(event);
            continue;
        }
        for (const earlier of held) out.push(earlier);
        held.length = 0;
        out.push(state.restart ? { ...event, restart: true } : event);
        state.restart = false;
        state.emitted = true;
    }
    return { terminal: null, held };
}

async function forward(out, first, model, context, options) {
    const state = { emitted: false, restart: false };
    let attempt = first;
    for (let n = 1; ; n++) {
        let ended;
        try {
            ended = await drain(attempt, out, state);
        } finally {
            attempt.release();
        }
        const { terminal, held } = ended;
        let closing = terminal;
        const cancelled = Boolean(options?.signal?.aborted);
        const stalled = attempt.stalled && !cancelled && terminal?.type === "error";
        const worthRetrying =
            !cancelled
            && n < MAX_ATTEMPTS
            && terminal?.type === "error"
            && (stalled || (!state.emitted && isTransientLlmFailure(terminal.error)));
        if (worthRetrying) {
            if (stalled) {
                console.warn(`[llm] no events for ${STREAM_IDLE_MS / 1000}s; retrying the call (attempt ${n + 1} of ${MAX_ATTEMPTS})`);
            }
            await sleep(retryDelayMs(n), options?.signal);
            if (!options?.signal?.aborted) {
                try {
                    attempt = await openAttempt(model, context, options);
                    state.restart = stalled && state.emitted;
                    continue;
                } catch (error) {
                    closing = restatedTerminal(terminal, error);
                }
            }
        } else if (stalled) {
            closing = stalledTerminal(terminal);
        }
        for (const earlier of held) out.push(earlier);
        if (closing) out.push(closing);
        else out.end();
        return;
    }
}

export async function retryingStreamFn(model, context, options) {
    const first = await openAttempt(model, context, options);
    const out = createAssistantMessageEventStream();
    void forward(out, first, model, context, options);
    return out;
}

let installed = false;

// Idempotent: repeat calls are free, so every Agent-constructing module can
// assert the runtime is present without coordinating who got there first.
export function installDefaultStreamFn() {
    if (installed) return;
    setDefaultStreamFn(retryingStreamFn);
    installed = true;
}

export function _isInstalledForTests() {
    return installed;
}
